// file: `react/src/components/ChatscopeDetails/ChatscopeDetails.tsx`:

import React, { useEffect, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import {
  Sidebar,
  ConversationHeader,
  ExpansionPanel,
  Search,
  Avatar,
  AddUserButton
} from '@chatscope/chat-ui-kit-react';
import { chatscopeAvatarSrc } from '../chatscopeAvatar.ts';
import { memberLabel } from '../MemberAvatars.tsx';
import UserSelect from '../UserSelect/UserSelect.tsx';
import { threadArchived, threadMuted, threadPinned } from '../threadPrefs.ts';
import { allowThreadInvites, roomSecurityLabel } from '../threadRoomType.ts';
import { allowEmailLookupForRoom } from '../../school/schoolClaims.ts';
import ThreadPhoto from '../ThreadPhoto.tsx';
import { expressURL, authFetch } from '../../utils/networkUtils';
import './ChatscopeDetails.scss';

function ChatscopeDetails({
  thread,
  messages = [],
  myId,
  myName,
  online = [],
  encryptionLocked,
  threadKey = null,
  threadQuery,
  onThreadQuery,
  onClose,
  onRename,
  onAddMembers,
  onRemoveMember,
  onTogglePin,
  onToggleMute,
  onToggleArchive,
  onDeleteConversation,
  onOpenImage
}) {
  const [name, setName] = useState(thread?.name || '');
  const [adding, setAdding] = useState(false);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [invitePath, setInvitePath] = useState('');

  const members = thread?.members || [];
  const photos = (messages || []).filter((msg) => msg.type === 'image' && msg.mediaUrl);
  const hasOwner = Boolean(thread?.ownerId);
  const isOwner = hasOwner && String(thread.ownerId) === String(myId);
  const inviteUrl = invitePath && typeof window !== 'undefined'
    ? `${window.location.origin}${invitePath}`
    : invitePath;

  useEffect(() => {
    setName(thread?.name || '');
  }, [thread?.name]);

  useEffect(() => {
    if (!thread?.id || !isOwner || !allowThreadInvites(thread?.roomType)) {
      setInvitePath('');
      return undefined;
    }
    let cancelled = false;
    authFetch(`${expressURL()}/api/threads/${thread.id}/invites`)
      .then(async (response) => (response.ok ? response.json() : []))
      .then((list) => {
        if (cancelled || !Array.isArray(list) || !list[0]?.path) return;
        setInvitePath(list[0].path);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [thread?.id, isOwner, thread?.roomType]);

  const createInvite = async () => {
    if (!thread?.id) return;
    setBusy(true);
    setError('');
    try {
      const response = await authFetch(`${expressURL()}/api/threads/${thread.id}/invites`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(body.error || 'Could not create invite');
      }
      setInvitePath(body.path || '');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create invite');
    } finally {
      setBusy(false);
    }
  };

  const copyInvite = async () => {
    if (!inviteUrl) return;
    try {
      await navigator.clipboard.writeText(inviteUrl);
    } catch (_) {
      setError('Could not copy the link');
    }
  };

  const revokeInvite = async () => {
    if (!thread?.id || !invitePath) return;
    const token = invitePath.slice('/join/'.length);
    setBusy(true);
    setError('');
    try {
      const response = await authFetch(
        `${expressURL()}/api/threads/${thread.id}/invites/${encodeURIComponent(token)}`,
        {
          method: 'DELETE'
        }
      );
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || 'Could not revoke invite');
      }
      setInvitePath('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not revoke invite');
    } finally {
      setBusy(false);
    }
  };

  const addPeople = async (event) => {
    event.preventDefault();
    if (!selected.length || !onAddMembers) return;
    setBusy(true);
    setError('');
    try {
      await onAddMembers(selected);
      setSelected([]);
      setAdding(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add that person');
    } finally {
      setBusy(false);
    }
  };

  const rename = async (event) => {
    event.preventDefault();
    const next = name.trim();
    if (!next || next === thread?.name || !onRename) return;
    setBusy(true);
    setError('');
    try {
      await onRename(next);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not rename');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sidebar position="right" scrollable className="chatscope-details-sidebar">
      <ConversationHeader>
        <ConversationHeader.Content
          userName="Details"
          info={roomSecurityLabel(thread, encryptionLocked)}
        />
        <ConversationHeader.Actions>
          <button type="button" className="chatscope-details-close" onClick={onClose}>Close</button>
        </ConversationHeader.Actions>
      </ConversationHeader>
      <Search
        placeholder="Search this chat"
        value={threadQuery || ''}
        onChange={(value) => onThreadQuery && onThreadQuery(value)}
        onClearClick={() => onThreadQuery && onThreadQuery('')}
      />
        <ExpansionPanel title="People" open>
        <ul className="chatscope-people">
          {members.map((member) => {
            const present = online.includes(member.shortName);
            const self = String(member.id) === String(myId) || member.shortName === myName;
            return (
              <li key={member.id || member.shortName}>
                <Avatar
                  src={chatscopeAvatarSrc(memberLabel(member))}
                  name={member.shortName}
                  status={present ? 'available' : 'unavailable'}
                  size="sm"
                />
                <div className="chatscope-people-copy">
                  <div className="chatscope-people-name">
                    {member.fullName || member.shortName}{self ? ' (you)' : ''}
                    {hasOwner && String(member.id) === String(thread.ownerId) ? ' · owner' : ''}
                  </div>
                  <div className="chatscope-people-status">{present ? 'Online' : 'Offline'}</div>
                </div>
                {self && !isOwner && (
                  <button
                    type="button"
                    className="chatscope-people-remove"
                    onClick={() => onRemoveMember && onRemoveMember(member)}
                  >
                    Leave
                  </button>
                )}
                {!self && isOwner && (
                  <button
                    type="button"
                    className="chatscope-people-remove"
                    onClick={() => onRemoveMember && onRemoveMember(member)}
                  >
                    Remove
                  </button>
                )}
              </li>
            );
          })}
        </ul>
        {isOwner && (adding ? (
          <Form className="chatscope-add-people" onSubmit={addPeople}>
            <UserSelect
              excludeUserIds={members.map((m) => m.id)}
              onChange={setSelected}
              allowEmailLookup={allowEmailLookupForRoom(thread?.roomType)}
            />
            {thread?.encryptionEnabled && encryptionLocked && (
              <p className="text-danger small mt-2 mb-0">Unlock encryption before adding someone here.</p>
            )}
            {error && <p className="text-danger small mt-2 mb-0">{error}</p>}
            <div className="mt-2">
              <Button size="sm" type="submit" disabled={busy || !selected.length}>Add</Button>
              <Button size="sm" variant="secondary" className="ms-2" type="button" onClick={() => setAdding(false)}>
                Cancel
              </Button>
            </div>
          </Form>
        ) : (
          <AddUserButton title="Add people" onClick={() => { setAdding(true); setError(''); }}>
            Add people
          </AddUserButton>
        ))}
      </ExpansionPanel>
      <ExpansionPanel title={`Photos (${photos.length})`}>
        {photos.length === 0 ? (
          <p className="chatscope-details-empty">No photos yet.</p>
        ) : (
          <div className="chatscope-photo-grid">
            {photos.map((msg) => (
              <div key={msg.id} className="chatscope-photo-cell">
                <ThreadPhoto
                  message={msg}
                  threadKey={threadKey}
                  encryptionLocked={encryptionLocked}
                  onOpen={onOpenImage}
                  alt=""
                />
              </div>
            ))}
          </div>
        )}
      </ExpansionPanel>
      <ExpansionPanel title="Chat settings">
        {isOwner ? (
          <Form onSubmit={rename} className="chatscope-rename">
            <Form.Label className="small mb-1">Conversation name</Form.Label>
            <Form.Control
              size="sm"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onFocus={() => setName(thread?.name || '')}
            />
            <Button size="sm" type="submit" className="mt-2" disabled={busy || !name.trim() || name.trim() === thread?.name}>
              Save name
            </Button>
          </Form>
        ) : (
          <p className="small text-muted px-3 mb-2">Only the owner can rename this conversation.</p>
        )}
        <div className="chatscope-pref-list">
          <button type="button" onClick={onTogglePin}>
            {threadPinned(thread) ? 'Unpin' : 'Pin'} this chat
          </button>
          <button type="button" onClick={onToggleMute}>
            {threadMuted(thread) ? 'Unmute' : 'Mute'} notifications
          </button>
          <button type="button" onClick={onToggleArchive}>
            {threadArchived(thread) ? 'Unarchive' : 'Archive'}
          </button>
          {isOwner && onDeleteConversation && (
            <button
              type="button"
              className="chatscope-delete-conversation"
              disabled={busy}
              onClick={async () => {
                const label = thread?.name ? `“${thread.name}”` : 'this conversation';
                if (!window.confirm(
                  `Delete ${label} for everyone? All messages and photos will be permanently removed. This cannot be undone.`
                )) {
                  return;
                }
                setBusy(true);
                setError('');
                try {
                  await onDeleteConversation();
                } catch (err) {
                  setError(err instanceof Error ? err.message : 'Could not delete conversation');
                  setBusy(false);
                }
              }}
            >
              Delete conversation
            </button>
          )}
        </div>
        {error && !adding && <p className="text-danger small mt-2 mb-0 px-3">{error}</p>}
      </ExpansionPanel>
      {isOwner && allowThreadInvites(thread?.roomType) && (
      <ExpansionPanel title="Invite link">
        <p className="small text-muted mb-2">
          Share a link so someone new can join this chat. The people picker stays limited to people
          you already chat with plus exact email lookup.
        </p>
        {invitePath ? (
          <>
            <Form.Control size="sm" readOnly value={inviteUrl} />
            <div className="mt-2">
              <Button size="sm" type="button" onClick={copyInvite} disabled={busy}>
                Copy link
              </Button>
              <Button
                size="sm"
                variant="secondary"
                className="ms-2"
                type="button"
                onClick={revokeInvite}
                disabled={busy}
              >
                Revoke
              </Button>
            </div>
          </>
        ) : (
          <Button size="sm" type="button" onClick={createInvite} disabled={busy}>
            Create invite link
          </Button>
        )}
      </ExpansionPanel>
      )}
    </Sidebar>
  );
}

export default ChatscopeDetails;
